import React from 'react';
import { get } from 'lodash';
import { BreakpointValue, BreakpointValueMap } from '@lendi-ui/breakpoint';
import { Wrap } from '@lendi-ui/grid';

import { UNIT_GUTTER } from '@funnels/constants';
import { useFormValues } from '@funnels/hooks';

import { FunnelGrid, FunnelGridUnit } from '../../_common';
import { TextAreaField } from '../../Fields';
import { CommonFieldProps } from '../../Fields/_common/types';
import { NeedsAndAnalysisCheckboxField } from './NeedsAndAnalysisCheckboxField/NeedsAndAnalysisCheckboxField';
import { NeedsAndAnalysisCheckbox, NeedsAndAnalysisCommon } from './NeedsAndAnalysisCheckboxVariationCustomerView';
import { NeedsAndAnalysisDatesTeamView } from './NeedsAndAnalysisDates';
import { NeedsAndAnalysisEstimatedImpactTeamView } from './NeedsAndAnalysisEstimatedImpact';

interface NeedsAndAnalysisCheckboxVariationTeamViewProps extends Omit<CommonFieldProps, 'name'> {
  isDisabled?: boolean;
  checkbox: NeedsAndAnalysisCheckbox;
  dates?: {
    startDate: NeedsAndAnalysisCommon;
    endDate: NeedsAndAnalysisCommon;
  };
  estimatedImpact?: {
    amount: NeedsAndAnalysisCommon;
    frequency: Omit<NeedsAndAnalysisCommon, 'label'>;
  };
  description?: NeedsAndAnalysisCommon;
  leftColumnSize?: BreakpointValue<number> | BreakpointValueMap<number>;
  rightColumnSize?: BreakpointValue<number> | BreakpointValueMap<number>;
  wrap?: BreakpointValue<Wrap> | BreakpointValueMap<Wrap>;
}

interface NeedsAndAnalysisDatesProps
  extends Omit<NeedsAndAnalysisCheckboxVariationTeamViewProps, 'checkbox' | 'estimatedImpact' | 'description'> {
  isChecked: string;
}

export const NeedsAndAnalysisDates = ({ isChecked, dates }: NeedsAndAnalysisDatesProps) => {
  if (dates) {
    return (
      <FunnelGridUnit mb="sm" size={{ mobile: 1 }}>
        <NeedsAndAnalysisDatesTeamView
          {...dates}
          isDisabled={!isChecked}
          key="checkbox-variation-dates-startdate-enddate"
        />
      </FunnelGridUnit>
    );
  }
  return null;
};

interface NeedsAndAnalysisEstimatedImpactProps
  extends Omit<NeedsAndAnalysisCheckboxVariationTeamViewProps, 'checkbox' | 'dates' | 'description'> {
  isChecked: string;
}

export const NeedsAndAnalysisEstimatedImpact = ({
  isChecked,
  estimatedImpact,
}: NeedsAndAnalysisEstimatedImpactProps) => {
  if (estimatedImpact) {
    return (
      <FunnelGridUnit mb="sm" size={{ mobile: 1 }}>
        <NeedsAndAnalysisEstimatedImpactTeamView
          {...estimatedImpact}
          isDisabled={!isChecked}
          key="checkbox-variation-estimatedimpact-amount-frequency"
        />
      </FunnelGridUnit>
    );
  }
  return null;
};

interface NeedsAndAnalysisDescriptionProps
  extends Omit<NeedsAndAnalysisCheckboxVariationTeamViewProps, 'checkbox' | 'dates' | 'estimatedImpact'> {
  isChecked: string;
}

export const NeedsAndAnalysisDescription = ({ isChecked, description }: NeedsAndAnalysisDescriptionProps) => {
  if (description) {
    return (
      <FunnelGridUnit width="100%">
        <TextAreaField
          {...description}
          placeholder="Type here..."
          isDisabled={!isChecked}
          isOptional={!isChecked}
          key="checkbox-variation-description"
        />
      </FunnelGridUnit>
    );
  }
  return null;
};

export const NeedsAndAnalysisCheckboxVariationTeamView: React.FC<NeedsAndAnalysisCheckboxVariationTeamViewProps> = (
  props
) => {
  const {
    isDisabled = false,
    checkbox: { name: checkboxName, label: checkboxLabel, value: checkboxValue },
    dates,
    estimatedImpact,
    description,
    leftColumnSize,
    rightColumnSize,
    wrap = { xs: true, lg: false },
  } = props;
  const [formValues] = useFormValues();
  const isChecked: string = get(formValues, checkboxName);

  return (
    <FunnelGrid gutter={UNIT_GUTTER.md} wrap={wrap}>
      <FunnelGridUnit size={leftColumnSize}>
        <NeedsAndAnalysisCheckboxField
          key="checkbox"
          name={checkboxName}
          checkboxLabel={checkboxLabel}
          value={checkboxValue}
          isDisabled={isDisabled}
        />
      </FunnelGridUnit>
      {(dates || estimatedImpact || description) && (
        <FunnelGridUnit size={rightColumnSize}>
          <FunnelGrid wrap={{ sm: true, lg: true }} direction="row">
            {(dates || estimatedImpact) && (
              <FunnelGrid valign="top">
                <NeedsAndAnalysisDates isChecked={isChecked} dates={dates} />
                <NeedsAndAnalysisEstimatedImpact isChecked={isChecked} estimatedImpact={estimatedImpact} />
              </FunnelGrid>
            )}
            <NeedsAndAnalysisDescription isChecked={isChecked} description={description} />
          </FunnelGrid>
        </FunnelGridUnit>
      )}
    </FunnelGrid>
  );
};
